import React, { useCallback, memo } from 'react';
import { Check, Mail, ChevronRight } from 'lucide-react';
import { ChatMessage as ChatMessageType, ChatAction } from '../../types/chat';
import { DatasetPreviewInline } from './DatasetPreviewInline';
import { formatMarkdown } from '../../utils/sanitization';
import { formatChatTimestamp } from '../../utils/dateUtils';
import skylarqLogo from '../../assets/skylarq_logo_small.png';

interface ChatMessageProps {
  message: ChatMessageType;
  onExecuteAction: (action: ChatAction) => void;
  showActions?: boolean;
}

const ChatMessageComponent: React.FC<ChatMessageProps> = ({ message, onExecuteAction }) => {
  const isUser = message.role === 'user';

  const handleGenerate = useCallback((params: any) => {
    onExecuteAction({
      type: 'generate_dataset',
      parameters: params,
      description: 'Generate dataset'
    });
  }, [onExecuteAction]);

  if (isUser) {
    return (
      <div className="flex justify-end group">
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
            {formatChatTimestamp(message.timestamp)}
          </span>
          <div className="max-w-[80%] bg-gray-900 text-white rounded-2xl rounded-br-sm px-4 py-2.5">
            <p
              className="text-[14px] leading-relaxed whitespace-pre-wrap"
              dangerouslySetInnerHTML={{ __html: formatMarkdown(message.content) }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-3 group">
      <img
        src={skylarqLogo}
        alt=""
        className="w-7 h-7 object-contain flex-shrink-0"
      />
      <div className="flex-1 min-w-0 pt-0.5">
        <div className="flex items-center gap-2 mb-1">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">SKYLARQ</p>
          <span className="text-[10px] text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
            {formatChatTimestamp(message.timestamp)}
          </span>
        </div>
        <p
          className="text-[15px] text-gray-800 leading-relaxed whitespace-pre-wrap"
          dangerouslySetInnerHTML={{ __html: formatMarkdown(message.content) }}
        />

        {/* Criteria Pills - shown when message has criteria array */}
        {message.criteria && message.criteria.length > 0 && (
          <div className="flex flex-col gap-2 mt-3 max-w-[320px]">
            {message.criteria.map((criterion, index) => (
              <div
                key={index}
                className="inline-flex items-start gap-2 px-3 py-2 text-[12px] font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg transition-all hover:bg-gray-100"
              >
                <Check className="w-3 h-3 flex-shrink-0 text-gray-400 mt-0.5" />
                <span className="leading-relaxed">{criterion}</span>
              </div>
            ))}
          </div>
        )}

        {/* Actions - always show when present */}
        {message.actions && message.actions.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {message.actions.map((action, index) => {
              // Special handling for generate_dataset action with preview data - show inline preview
              if (action.type === 'generate_dataset' && action.parameters?.previewData) {
                return (
                  <DatasetPreviewInline
                    key={index}
                    initialParams={{
                      searchQuery: action.parameters.searchQuery || '',
                      tableType: action.parameters.tableType || 'companies',
                      customTypeDescription: action.parameters.customTypeDescription,
                      rowCount: action.parameters.rowCount
                    }}
                    onGenerate={handleGenerate}
                  />
                );
              }

              // Campaign/email actions get a card-style presentation
              if (action.type === 'send_emails') {
                return (
                  <button
                    key={index}
                    onClick={() => onExecuteAction(action)}
                    className="w-full p-4 bg-white border border-gray-200 rounded-xl hover:border-primary-300 hover:shadow-sm transition-all text-left group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-primary-100 transition-colors">
                        <Mail className="w-5 h-5 text-primary-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-medium text-gray-900">Send Email Campaign</p>
                        <p className="text-[11px] text-gray-500 truncate">
                          {action.parameters?.count ? `${action.parameters.count} recipients` : 'Selected leads'}
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors" />
                    </div>
                  </button>
                );
              }

              // Regular action buttons - styled as pills with better overflow handling
              return (
                <button
                  key={index}
                  onClick={() => onExecuteAction(action)}
                  className="px-4 py-2 text-[13px] font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 active:bg-gray-300 transition-colors max-w-full"
                >
                  <span className="line-clamp-2">{action.description || `Execute ${action.type.replace(/_/g, ' ')}`}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// Memoize to prevent re-renders when parent state changes (e.g., typewriter effect)
export const ChatMessage = memo(ChatMessageComponent, (prevProps, nextProps) => {
  // Only re-render if message content, id, or criteria changes
  return (
    prevProps.message.id === nextProps.message.id &&
    prevProps.message.content === nextProps.message.content &&
    JSON.stringify(prevProps.message.criteria) === JSON.stringify(nextProps.message.criteria)
  );
});
